import React, { useEffect, useState, useMemo } from "react";
import { apiGet } from "../../../services/api";
import { safeDate } from "../../../utils/dateUtils";
import Button from "../../common/Button";
import "./CalendarDayView.css";

interface VisitRow {
  visit_id?: string;
  patient_id?: string | number;
  patient_name?: string;
  phone?: string;
  visit_type?: string;
  gender?: string;
  age?: string | number;
  status?: string;
  created_at?: string | number;
  appointment_time?: string | number;
  has_records?: boolean;
  has_prescriptions?: boolean;
}

const getVisitColor = (type: string | undefined): string => {
  const t = (type || "").toLowerCase();
  if (t.includes("surgery") || t.includes("implant")) return "red";
  if (t.includes("consult")) return "blue";
  if (t.includes("rct") || t.includes("endo")) return "purple";
  return "teal";
};

const getInitials = (name: string | undefined): string =>
  (name || "Unknown").split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase();

function formatVisitTime(v: VisitRow): string {
  const raw = v.appointment_time ?? v.created_at;
  const d = safeDate(raw);
  return d ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
}

export interface CalendarDayViewProps {
  selectedDate?: string | null;
  onSelectVisit?: (visit: VisitRow) => void;
  visits?: VisitRow[] | null;
  loading?: boolean;
}

export default function CalendarDayView({ selectedDate, onSelectVisit, visits: visitsProp, loading: loadingProp }: CalendarDayViewProps) {
  const [internalVisits, setInternalVisits] = useState<VisitRow[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const useParentData = Array.isArray(visitsProp) || loadingProp === true;
  const visits = useParentData ? (visitsProp || []) : internalVisits;
  const loading = useParentData ? (loadingProp ?? false) : internalLoading;

  useEffect(() => {
    if (useParentData || !selectedDate) return;

    setInternalLoading(true);
    apiGet(`/api/visits-by-date?date=${selectedDate}`)
      .then((value: unknown) => {
        const data = value as { visits?: VisitRow[] };
        const list = Array.isArray(data?.visits) ? data.visits : [];
        setInternalVisits(list);
      })
      .catch((err: unknown) => {
        console.error("Schedule fetch failed", err);
        setInternalVisits([]);
      })
      .finally(() => setInternalLoading(false));
  }, [selectedDate, useParentData]);

  const sortedVisits = useMemo(() => {
    const list = [...(visits || [])];
    list.sort((a, b) => {
      const tA = safeDate(a.appointment_time ?? a.created_at)?.getTime() || (a.visit_id as unknown as number) || 0;
      const tB = safeDate(b.appointment_time ?? b.created_at)?.getTime() || (b.visit_id as unknown as number) || 0;
      return tA - tB;
    });
    return list;
  }, [visits]);

  const normStatus = (raw: unknown): string => String(raw ?? "").trim().toUpperCase().replace(/\s+/g, "_");
  const isDone = (s: string): boolean => {
    if (!s) return false;
    return s.includes("LOCK") || s.includes("COMPLETE") || s === "DONE";
  };

  const filteredVisits = useMemo(
    () =>
      sortedVisits.filter((v) =>
        (v.patient_name || "").toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [sortedVisits, searchTerm]
  );

  const stats = useMemo(
    () => ({
      total: sortedVisits.length,
      done: sortedVisits.filter((v) => isDone(normStatus(v.status))).length,
      pending: sortedVisits.filter((v) => !isDone(normStatus(v.status))).length,
    }),
    [sortedVisits]
  );

  return (
    <div className="calendar-day-view">
      
      <div className="day-header-glass">
        <div className="header-main">
          <div className="date-block">
            <div className="weekday">
              {safeDate(selectedDate)?.toLocaleDateString("en-US", { weekday: "long" }) ?? "—"}
            </div>
            <div className="full-date">
              {safeDate(selectedDate)?.toLocaleDateString("en-US", { month: "long", day: "numeric" }) ?? "—"}
            </div>
          </div>
          <div className="header-stats">
            <div className="stat-pill">
              <span className="lbl">Total</span>
              <span className="val">{stats.total}</span>
            </div>
            <div className="stat-pill done">
              <span className="lbl">Done</span>
              <span className="val">{stats.done}</span>
            </div>
            <div className="stat-pill pending">
              <span className="lbl">Left</span>
              <span className="val">{stats.pending}</span>
            </div>
          </div>
        </div>
        <div className="search-row">
          <input
            type="text"
            placeholder="🔍 Find patient in schedule..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="schedule-scroll-area">
        {loading ? (
          <div className="mod-empty-state">Loading schedule...</div>
        ) : filteredVisits.length === 0 ? (
          <div className="mod-empty-state">No visits scheduled for today.</div>
        ) : (
          <div className="timeline-container">
            {filteredVisits.map((v, i) => {
              const isLocked = isDone(normStatus(v.status));
              const typeColor = getVisitColor(v.visit_type);
              
              return (
                <div key={v.visit_id} className={`timeline-row ${isLocked ? "done" : ""}`} onClick={() => onSelectVisit?.(v)}>
                  <div className="time-col">
                    <div className="timeline-line">
                      <span className="dot" />
                    </div>
                    <span className="time-text">
                    {formatVisitTime(v)}
                    </span>
                  </div>
                  
                  <div className="card-col">
                    <div
                      className="appt-card"
                      style={{
                        ["--appt-accent" as string]: `var(--${typeColor}-500, #3b82f6)`,
                        ["--appt-bg" as string]: `var(--${typeColor}-100, #eff6ff)`,
                        ["--appt-fg" as string]: `var(--${typeColor}-700, #1d4ed8)`,
                      }}
                    >
                    <div className="appt-accent" />
                    
                    <div className="appt-inner">
                      <div className="card-left">
                        <div className="avatar" style={{ background: "var(--appt-bg)", color: "var(--appt-fg)" }}>
                          {getInitials(v.patient_name)}
                        </div>
                        <div>
                          <h4 className="appt-title">
                            {v.patient_name}
                            {isLocked && <span className="appt-tag">DONE</span>}
                          </h4>
                          <div className="appt-meta">
                            <span>{v.visit_type || "Checkup"}</span>
                            {v.age && <span>• {v.age}y</span>}
                            {v.gender && <span>• {v.gender}</span>}
                          </div>
                        </div>
                      </div>

                      <div className="appt-actions">
                        <div className="appt-icons">
                          {v.has_records && <span title="Clinical Notes Present">📝</span>}
                          {v.has_prescriptions && <span title="Prescription Issued">💊</span>}
                        </div>
                        <Button variant={isLocked ? 'outline' : 'primary'} size="sm" onClick={(e) => { e.stopPropagation(); onSelectVisit?.(v); }}>
                          {isLocked ? "Review" : "Open Chart"}
                        </Button>
                      </div>
                    </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
